import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  EmojiEvents as TierIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { adminReferralApi } from '../../api';
import type { ReferralRewardTier, CreateTierInput, UpdateTierInput } from '../../api';
import { useDocumentTitle } from '../../hooks/core';

export function ReferralTiersPage() {
  useDocumentTitle('Referral Tiers');

  const [tiers, setTiers] = useState<ReferralRewardTier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showInactive, setShowInactive] = useState(false);

  // Create tier dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [newTier, setNewTier] = useState<CreateTierInput>({
    minReferrals: 0,
    creditsAwarded: 0,
    name: '',
    description: '',
  });

  // Edit tier dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [editTier, setEditTier] = useState<ReferralRewardTier | null>(null);
  const [editInput, setEditInput] = useState<UpdateTierInput>({});

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const tiersData = await adminReferralApi.listTiers(showInactive);
      setTiers(tiersData.sort((a, b) => a.minReferrals - b.minReferrals));
      setError(null);
    } catch (err) {
      setError('Failed to load tiers');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [showInactive]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateTier = async () => {
    try {
      setCreateLoading(true);
      setCreateError(null);
      await adminReferralApi.createTier(newTier);
      setSuccess('Tier created successfully');
      setCreateDialogOpen(false);
      resetCreateForm();
      fetchData();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create tier';
      setCreateError(errorMessage);
    } finally {
      setCreateLoading(false);
    }
  };

  const handleUpdateTier = async () => {
    if (!editTier) return;
    try {
      setEditLoading(true);
      setEditError(null);
      await adminReferralApi.updateTier(editTier.id, editInput);
      setSuccess('Tier updated successfully');
      setEditDialogOpen(false);
      fetchData();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update tier';
      setEditError(errorMessage);
    } finally {
      setEditLoading(false);
    }
  };

  const handleDeleteTier = async (tier: ReferralRewardTier) => {
    if (!confirm(`Are you sure you want to delete the tier "${tier.name}"?`)) return;
    try {
      await adminReferralApi.deleteTier(tier.id);
      setSuccess('Tier deleted successfully');
      fetchData();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete tier';
      setError(errorMessage);
    }
  };

  const resetCreateForm = () => {
    setNewTier({
      minReferrals: 0,
      creditsAwarded: 0,
      name: '',
      description: '',
    });
    setCreateError(null);
  };

  const openEditDialog = (tier: ReferralRewardTier) => {
    setEditTier(tier);
    setEditInput({
      minReferrals: tier.minReferrals,
      creditsAwarded: tier.creditsAwarded,
      name: tier.name,
      description: tier.description,
      active: tier.active,
    });
    setEditError(null);
    setEditDialogOpen(true);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <TierIcon color="primary" sx={{ fontSize: 32 }} />
          <Typography variant="h4">Referral Reward Tiers</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
              />
            }
            label="Show inactive"
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Create Tier
          </Button>
        </Box>
      </Box>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Configure the reward tiers for the referral program. Users earn credits when they reach each tier's referral threshold.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Tier Name</TableCell>
                <TableCell>Min Referrals</TableCell>
                <TableCell>Credits Awarded</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tiers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No tiers found. Create a tier to get started.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                tiers.map((tier) => (
                  <TableRow key={tier.id} hover>
                    <TableCell>
                      <Typography fontWeight="medium">
                        {tier.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={`${tier.minReferrals} referrals`}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={`+${tier.creditsAwarded} credit${tier.creditsAwarded !== 1 ? 's' : ''}`}
                        size="small"
                        color="success"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {tier.description || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={tier.active ? 'Active' : 'Inactive'}
                        size="small"
                        color={tier.active ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEditDialog(tier)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => handleDeleteTier(tier)}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create Tier Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Reward Tier</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {createError && (
              <Alert severity="error" onClose={() => setCreateError(null)}>
                {createError}
              </Alert>
            )}
            <TextField
              label="Tier Name"
              value={newTier.name}
              onChange={(e) => setNewTier({ ...newTier, name: e.target.value })}
              helperText="e.g., Bronze Referrer, Silver Referrer"
              fullWidth
              required
            />
            <TextField
              label="Minimum Referrals"
              type="number"
              value={newTier.minReferrals || ''}
              onChange={(e) => setNewTier({ ...newTier, minReferrals: parseInt(e.target.value) || 0 })}
              helperText="Number of referrals needed to unlock this tier"
              fullWidth
              required
              inputProps={{ min: 1 }}
            />
            <TextField
              label="Credits Awarded"
              type="number"
              value={newTier.creditsAwarded || ''}
              onChange={(e) => setNewTier({ ...newTier, creditsAwarded: parseInt(e.target.value) || 0 })}
              helperText="Number of free board credits to award"
              fullWidth
              required
              inputProps={{ min: 1 }}
            />
            <TextField
              label="Description"
              value={newTier.description}
              onChange={(e) => setNewTier({ ...newTier, description: e.target.value })}
              helperText="Optional description shown to users"
              fullWidth
              multiline
              rows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateTier}
            variant="contained"
            disabled={createLoading || !newTier.name || !newTier.minReferrals || !newTier.creditsAwarded}
          >
            {createLoading ? <CircularProgress size={24} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Tier Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Tier: {editTier?.name}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {editError && (
              <Alert severity="error" onClose={() => setEditError(null)}>
                {editError}
              </Alert>
            )}
            <TextField
              label="Tier Name"
              value={editInput.name || ''}
              onChange={(e) => setEditInput({ ...editInput, name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Minimum Referrals"
              type="number"
              value={editInput.minReferrals || ''}
              onChange={(e) => setEditInput({ ...editInput, minReferrals: parseInt(e.target.value) || 0 })}
              fullWidth
              required
              inputProps={{ min: 1 }}
            />
            <TextField
              label="Credits Awarded"
              type="number"
              value={editInput.creditsAwarded || ''}
              onChange={(e) => setEditInput({ ...editInput, creditsAwarded: parseInt(e.target.value) || 0 })}
              fullWidth
              required
              inputProps={{ min: 1 }}
            />
            <TextField
              label="Description"
              value={editInput.description || ''}
              onChange={(e) => setEditInput({ ...editInput, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={editInput.active ?? editTier?.active ?? true}
                  onChange={(e) => setEditInput({ ...editInput, active: e.target.checked })}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleUpdateTier}
            variant="contained"
            disabled={editLoading}
          >
            {editLoading ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
